"use client"

import Link from 'next/link'
import { useState } from 'react'
import { Instagram, Facebook, MessageCircle, UserCircle, LogIn, LogOut, Menu, X } from 'lucide-react'
import { ModeToggle } from '@/components/ui/mode-toggle'
import { useFrontSession } from '@/components/front-session-provider'
import { logoutAction } from '@/app/(auth)/auth/actions'
import { canAccessProjects } from '@/integrity/iam/permissions'

interface GlobalHeaderProps {
  onCheckoutClick?: () => void
}

export function GlobalHeader({ onCheckoutClick }: GlobalHeaderProps) {
  const { authenticated, roles } = useFrontSession()
  const canViewProjects = canAccessProjects(roles)
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  return (
    <header
      id="global-site-header"
      className="fixed top-0 left-0 right-0 z-[100] bg-background/80 backdrop-blur-md border-b border-border text-foreground transition-colors duration-300 dark:bg-black/80 dark:border-white/10 dark:text-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          
          {/* Left Side: Logo, Auth, Socials */}
          <div className="flex items-center space-x-6">
            <Link href="/" className="text-xl font-bold tracking-tighter hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
              Akiles
            </Link>

            <div className="hidden md:flex items-center space-x-4">
              {authenticated ? (
                <form action={logoutAction}>
                  <button
                    type="submit"
                    className="flex items-center space-x-1 text-sm font-medium hover:text-muted-foreground transition-colors dark:hover:text-gray-300"
                  >
                    <span>Log Out</span>
                  </button>
                </form>
              ) : (
                <Link href="/auth/login" className="flex items-center space-x-1 text-sm font-medium hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                  <span>Log In</span>
                </Link>
              )}

              <div className="flex items-center space-x-3 border-l border-border pl-4 dark:border-white/10">
                <a
                  href="https://www.instagram.com/akiles.artstudio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-muted-foreground transition-colors dark:hover:text-gray-300"
                >
                  <Instagram className="h-4 w-4" />
                </a>
                <a
                  href="https://www.facebook.com/AkilesArtStudio"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-muted-foreground transition-colors dark:hover:text-gray-300"
                >
                  <Facebook className="h-4 w-4" />
                </a>
                <a
                  href="https://wa.me/50663212319?text=¡Hola!%20Vengo%20de%20la%20página%20web%20y%20quisiera%20más%20información."
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-muted-foreground transition-colors dark:hover:text-gray-300"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Side: Main Nav */}
          <div className="hidden lg:flex items-center space-x-6">
            <nav className="flex items-center space-x-6 mr-6">
              <Link href="/store" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Store
              </Link>
              <Link href="/portfolio" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Portfolio
              </Link>
              <Link href="/bio" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Bio
              </Link>
              <Link href="/projects" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Projects
              </Link>
              <Link href="/classes" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Classes
              </Link>
              <Link href="/maker-space" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Maker Space
              </Link>
              <Link href="/contact" className="text-sm font-bold hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                Contact
              </Link>
            </nav>

            <div className="flex items-center space-x-4 border-l border-border pl-6 dark:border-white/10">
              <Link href="/account" className="hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                <UserCircle className="h-5 w-5" />
              </Link>
              <ModeToggle />
              
              {/* DEV TOGGLE FOR FOUNDER */}
              {roles.some((r) => r.toLowerCase() === 'founder') && (
                <button
                  onClick={() => {
                    if (typeof window !== 'undefined') {
                      window.dispatchEvent(new CustomEvent('cycleDevView'));
                    }
                  }}
                  className="p-2 rounded-full bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-800 dark:hover:bg-zinc-700 transition-colors border border-border/50 shadow-sm"
                  title="Cycle Responsive View"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-monitor">
                    <rect width="20" height="14" x="2" y="3" rx="2"/><line x1="8" x2="16" y1="21" y2="21"/><line x1="12" x2="12" y1="17" y2="21"/>
                  </svg>
                </button>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="lg:hidden flex items-center space-x-4">
             <Link href="/account" className="hover:text-muted-foreground transition-colors dark:hover:text-gray-300">
                <UserCircle className="h-5 w-5" />
              </Link>
              <ModeToggle />
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 -mr-2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Toggle mobile menu"
              >
                {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
              </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Dropdown */}
      {mobileMenuOpen && (
        <div className="lg:hidden absolute top-16 left-0 right-0 bg-background border-b border-border shadow-2xl p-6 flex flex-col space-y-4 max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="flex flex-col space-y-4">
            <Link href="/store" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Store</Link>
            <Link href="/portfolio" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Portfolio</Link>
            <Link href="/bio" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Bio</Link>
            <Link href="/projects" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Projects</Link>
            <Link href="/classes" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-primary transition-colors">Classes</Link>
            <Link href="/maker-space" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Maker Space</Link>
            <Link href="/contact" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Contact</Link>
            
            <div className="border-t border-border pt-4 mt-4 flex flex-col space-y-4">
              {authenticated ? (
                <form action={logoutAction}>
                  <button type="submit" className="text-lg font-bold text-destructive text-left w-full">Log Out</button>
                </form>
              ) : (
                <Link href="/auth/login" onClick={() => setMobileMenuOpen(false)} className="text-lg font-bold hover:text-muted-foreground">Log In</Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  )
}
